import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../lib/cn.js';
import { useFieldContext } from './Field.js';

export const CONTROL_CLASS =
  'w-full rounded-sm border border-line bg-bg px-2.5 py-1.5 text-nav text-ink placeholder:text-mute transition-colors hover:border-mute/60 focus:border-brand focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-brand';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Mono for IDs, keys, URLs. */
  mono?: boolean;
  /** 12px leading icon inside the field. */
  icon?: LucideIcon;
  /** Trailing content inside the field (Kbd hint, unit). */
  trailing?: React.ReactNode;
}

/** Text input: bg --bg inset, border --line, focus border --brand. Inherits Field ids. */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, mono = false, icon: Icon, trailing, id, 'aria-describedby': describedBy, 'aria-invalid': invalid, required, ...rest },
  ref,
) {
  const field = useFieldContext();
  const control = (
    <input
      ref={ref}
      id={id ?? field?.id}
      aria-describedby={describedBy ?? field?.describedBy}
      aria-invalid={invalid ?? (field?.invalid ? true : undefined)}
      required={required ?? field?.required}
      className={cn(CONTROL_CLASS, mono ? 'font-mono text-xs' : '', Icon ? 'pl-7' : '', trailing ? 'pr-12' : '', className)}
      {...rest}
    />
  );
  if (!Icon && !trailing) return control;
  return (
    <span className="relative block w-full">
      {Icon ? <Icon size={12} aria-hidden className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-mute" /> : null}
      {control}
      {trailing ? <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 flex items-center">{trailing}</span> : null}
    </span>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  mono?: boolean;
}

/** Multi-line input with the same chrome as Input. Default 4 rows, vertical resize only. */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, mono = false, id, rows = 4, 'aria-describedby': describedBy, 'aria-invalid': invalid, required, ...rest },
  ref,
) {
  const field = useFieldContext();
  return (
    <textarea
      ref={ref}
      id={id ?? field?.id}
      rows={rows}
      aria-describedby={describedBy ?? field?.describedBy}
      aria-invalid={invalid ?? (field?.invalid ? true : undefined)}
      required={required ?? field?.required}
      className={cn(CONTROL_CLASS, 'resize-y leading-snug', mono ? 'font-mono text-xs' : '', className)}
      {...rest}
    />
  );
});
